/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {
  Card,
  Text,
  VStack,
  Box,
  HStack,
  Image,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Badge,
  Tooltip,
  Button,
  Checkbox,
  TableContainer,
} from '@chakra-ui/react';
import { Product } from './interfaces';
import { AgrementProductItem } from './atoms/AgrementPlanItem';

// Badges definidos localmente para evitar dependência circular
const badges = [
  {
    name: 'Bronze',
    level: 1,
    image: 'badges/bronze.png',
  },
  {
    name: 'Prata',
    level: 2,
    image: 'badges/prata.png',
  },
  {
    name: 'Ouro',
    level: 3,
    image: 'badges/ouro.png',
  },
  {
    name: 'Safira',
    level: 4,
    image: 'badges/safira.png',
  },
  {
    name: 'Diamante',
    level: 5,
    image: 'badges/diamante.png',
  },
];

// Interface para planos ERP
interface IPlanERP {
  id: number;
  id_plan: number;
  name_plan: string;
  value_plan: number;
  value?: number;
  count_clients_active: number;
  count_clients?: number;
  plan_slug_integration?: string;
  erp: string;
  products?: Array<{ product: number; plan: number; map_integration_plan: number; active?: boolean }>;
}

interface Step3ConfiguredPlansProps {
  products: Product[];
  filteredAndSortedProductsERP: IPlanERP[];
  sortOrder?: 'asc' | 'desc' | null;
  setSortOrder?: (order: 'asc' | 'desc' | null) => void;
}

export const Step3ConfiguredPlans: React.FC<Step3ConfiguredPlansProps> = ({
  products,
  filteredAndSortedProductsERP,
  sortOrder,
  setSortOrder,
}) => {
  if (!filteredAndSortedProductsERP || !Array.isArray(filteredAndSortedProductsERP) || filteredAndSortedProductsERP.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
        Nenhum plano encontrado com os critérios de busca
      </Text>
    );
  }

  // Aplicar ordenação aos dados
  const sortedProductsERP = [...filteredAndSortedProductsERP].sort((a, b) => {
    if (!sortOrder) return 0;

    const valueA = a.value_plan || 0;
    const valueB = b.value_plan || 0;

    if (sortOrder === 'asc') {
      return valueA - valueB;
    } else {
      return valueB - valueA;
    }
  });

  // Agrupar produtos por tipo (SVA = default_product.id === 4, igual aos outros steps)
  const produtosPartiuProducts = products.filter((product: any) =>
    product.default_product?.id !== 4
  );

  const svaProducts = products.filter((product: any) =>
    product.default_product?.id === 4
  );

  // Ordenar produtos: E-Book, AudioBook, Super App, Revista, depois SVAs
  const sortedProducts = [
    ...produtosPartiuProducts,
    ...svaProducts
  ];

  // Contar produtos por categoria para agrupamento
  const productGrouping = {
    produtosPartiu: produtosPartiuProducts.length,
    svas: svaProducts.length,
  };


  // Calcular largura total para scroll horizontal (alinhado com HeaderTable: 60 + 250 + 100 + 120)
  const totalWidth = 60 + 250 + 100 + 120 + (sortedProducts.length * 150);

  return (
    <VStack spacing={2} width="100%">
      <Card width="100%" boxShadow="sm">
        <TableContainer maxH="70vh" overflowY="auto" overflowX="auto" position="relative" sx={{ '&::-webkit-scrollbar': { width: '8px', height: '8px' }, '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '4px' }, '&::-webkit-scrollbar-thumb:hover': { background: '#a8a8a8' } }}>
          {/* Wrapper sticky: agrupamento + cabeçalho ficam no topo ao rolar (igual Step1) */}
          <Box
            position="sticky"
            top={0}
            zIndex={20}
            bg="gray.50"
            minWidth={`${totalWidth}px`}
            width={`${totalWidth}px`}
            boxShadow="0 2px 4px rgba(0,0,0,0.08)"
          >
            {/* Linha de Agrupamento */}
            <Box
              display="grid"
              gridTemplateColumns={`60px 250px 100px 120px repeat(${Math.max(0, sortedProducts.length)}, 150px)`}
              bg="gray.50"
              borderBottom="0px solid"
              borderColor="gray.200"
              px={0}
              minWidth={`${totalWidth}px`}
              width={`${totalWidth}px`}
            >
              {/* Fixed columns spacers */}
              <Box width="60px" bg="gray.50" />
              <Box width="250px" bg="gray.50" />
              <Box width="100px" bg="gray.50" />
              <Box width="120px" bg="gray.50" />

              {/* Agrupamento Produtos Partiu - só renderiza se > 0 */}
              {productGrouping.produtosPartiu > 0 && (
                <Box
                  gridColumn={`span ${productGrouping.produtosPartiu}`}
                  textAlign="center"
                  fontSize="xs"
                  fontWeight="extrabold"
                  color="gray.700"
                  minW="150px"
                  flexShrink={0}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  textTransform="uppercase"
                  bg="gray.50"
                  py={2}
                  borderLeft="1px solid"
                  px={2}
                  borderColor="gray.200"
                >
                  Produtos Partiu
                </Box>
              )}

              {/* Agrupamento SVAs - só renderiza se > 0 */}
              {productGrouping.svas > 0 && (
                <Box
                  gridColumn={`span ${productGrouping.svas}`}
                  textAlign="center"
                  fontSize="xs"
                  fontWeight="extrabold"
                  color="gray.700"
                  minW="150px"
                  flexShrink={0}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  textTransform="uppercase"
                  bg="gray.50"
                  py={2}
                  px={2}
                  borderLeft="1px solid"
                  borderColor="gray.200"
                >
                  Outros Produtos
                </Box>
              )}
            </Box>

            {/* Cabeçalho do Grid (ID, Nome, Valor, Usuários, colunas produtos) */}
            <Box
              display="grid"
              gridTemplateColumns={`60px 250px 100px 120px repeat(${Math.max(0, sortedProducts.length)}, 150px)`}
              bg="gray.50"
              borderBottom="2px solid"
              borderColor="gray.300"
              px={0}
              minWidth={`${totalWidth}px`}
              width={`${totalWidth}px`}
            >
            {/* ID */}
            <Box
              fontSize="xs"
              color="gray.700"
              fontWeight="bold"
              textTransform="uppercase"
              borderRight="1px solid"
              borderColor="gray.200"
              textAlign="center"
              width="60px"
              minW="60px"
              maxW="60px"
              flexShrink={0}
              bg="gray.50"
              py={3}
              position="sticky"
              left={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              zIndex={12}
            >
              ID
            </Box>

            {/* Nome do Plano */}
            <Box
              fontSize="xs"
              color="gray.700"
              fontWeight="bold"
              textTransform="uppercase"
              textAlign="left"
              width="250px"
              minW="250px"
              maxW="250px"
              flexShrink={0}
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              px={3}
              borderRight="1px solid"
              borderColor="gray.200"
              bg="gray.50"
              py={3}
              position="sticky"
              left="60px"
              zIndex={12}
            >
              Nome do Plano
            </Box>

            {/* Valor do Plano */}
            <Box
              fontSize="xs"
              color="gray.700"
              fontWeight="bold"
              textTransform="uppercase"
              textAlign="center"
              width="100px"
              minW="100px"
              maxW="100px"
              flexShrink={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="gray.50"
              py={2}
              flexDirection="row"
              gap={2}
              borderRight="1px solid"
              borderColor="gray.200"
              position="sticky"
              left="310px"
              zIndex={12}
            >
              <Text fontSize="12px" fontWeight="bold" whiteSpace="nowrap">VALOR</Text>
              {setSortOrder && (
                <Box
                  as="button"
                  onClick={() => {
                    if (sortOrder === null) {
                      setSortOrder('asc');
                    } else if (sortOrder === 'asc') {
                      setSortOrder('desc');
                    } else {
                      setSortOrder(null);
                    }
                  }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  w="20px"
                  h="20px"
                  borderRadius="4px"
                  bg={sortOrder ? 'blue.50' : 'gray.50'}
                  border="1px solid"
                  borderColor={sortOrder ? 'blue.200' : 'gray.200'}
                  color={sortOrder ? 'blue.600' : 'gray.400'}
                  cursor="pointer"
                  _hover={{
                    bg: sortOrder ? 'blue.100' : 'gray.100',
                    borderColor: sortOrder ? 'blue.300' : 'gray.300',
                    transform: 'translateY(-1px)',
                  }}
                  transition="all 0.2s ease"
                  title={
                    sortOrder === null
                      ? 'Ordenar por valor'
                      : sortOrder === 'asc'
                        ? 'Menor para maior'
                        : 'Maior para menor'
                  }
                >
                  <Text fontSize="10px">
                    {sortOrder === null ? '↕' : sortOrder === 'asc' ? '↑' : '↓'}
                  </Text>
                </Box>
              )}
            </Box>

            {/* Usuários Ativos */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="xs"
              color="gray.700"
              fontWeight="bold"
              textTransform="uppercase"
              borderRight="1px solid"
              borderColor="gray.200"
              py={3}
              width="120px"
              minW="120px"
              maxW="120px"
              flexShrink={0}
              bg="gray.50"
            >
              <VStack spacing={0}>
                <Text fontSize="xs" whiteSpace="nowrap">USUÁRIOS</Text>
                <Text fontSize="xs" whiteSpace="nowrap">ATIVOS</Text>
              </VStack>
            </Box>

            {/* Colunas dos Produtos */}
            {sortedProducts.map((product) => {
              const isContracted = product.active;
              return (
                <Box
                  key={product.name}
                  fontSize="xs"
                  color="gray.700"
                  fontWeight="bold"
                  textTransform="uppercase"
                  textAlign="center"
                  bg="gray.50"
                  opacity={isContracted ? 1 : 0.5}
                  borderRight="1px solid"
                  borderColor="gray.200"
                  py={2}
                  px={2}
                  minW="150px"
                  maxW="150px"
                  flexShrink={0}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  <AgrementProductItem product={product as any} />
                </Box>
              );
            })}
          </Box>
          </Box>

          {/* Linhas do Grid com Acordeão */}
          <Box as="div" minWidth={`${totalWidth}px`} width={`${totalWidth}px`}>
          <Accordion allowToggle>
            {sortedProductsERP.map((productERP: any) => (
              <AccordionItem key={productERP.id} border="none" borderBottom="1px solid" borderColor="gray.200">
                {() => (
                  <>
                    <AccordionButton
                      p={0}
                      _hover={{ bg: "gray.50" }}
                      _focus={{ boxShadow: "none" }}
                    >
                      <Box
                        display="grid"
                        gridTemplateColumns={`60px 250px 100px 120px repeat(${Math.max(0, sortedProducts.length)}, 150px)`}
                        minWidth={`${totalWidth}px`}
                        width={`${totalWidth}px`}
                        minH="50px"
                        alignItems="center"
                      >
                        {/* ID */}
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          borderRight="1px solid"
                          borderColor="gray.200"
                          py={1}
                          height="50px"
                          position="sticky"
                          left={0}
                          zIndex={12}
                          bg="white"
                          width="60px"
                          minW="60px"
                          maxW="60px"
                        >
                          <Tooltip label="Identificador único do plano ERP no sistema" placement="top">
                            <Text fontSize="sm" fontWeight="bold" color="gray.600" maxW="60px">
                              {productERP.id_plan ?? productERP.id ?? '-'}
                            </Text>
                          </Tooltip>
                        </Box>

                        {/* Nome do Plano */}
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-start"
                          borderRight="1px solid"
                          borderColor="gray.200"
                          py={1}
                          position="sticky"
                          left="60px"
                          zIndex={12}
                          bg="white"
                          px={3}
                          width="250px"
                          minW="250px"
                          maxW="250px"
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="medium"
                            color="gray.800"
                            lineHeight="1.2"
                            wordBreak="break-word"
                            textAlign="left"
                            whiteSpace="normal"
                            maxW="100%"
                          >
                            {productERP.name_plan || productERP.plan_slug_integration || `Plano #${productERP.id_plan ?? productERP.id}`}
                          </Text>
                        </Box>

                        {/* Valor do Plano */}
                        <Box
                          textAlign="center"
                          py={1}
                          px={2}
                          borderRight="1px solid"
                          borderColor="gray.200"
                          width="100px"
                          position="sticky"
                          left="310px"
                          zIndex={12}
                          bg="white"
                          minW="100px"
                          maxW="100px"
                        >
                          <Tooltip label="Valor mensal do plano ERP" placement="top">
                            <Box
                              bg="blue.50"
                              border="1px solid"
                              borderColor="blue.200"
                              color="blue.700"
                              px={2}
                              py={1}
                              borderRadius="md"
                              fontSize="xs"
                              fontWeight="semibold"
                              textAlign="center"
                              _hover={{
                                bg: 'blue.100',
                                borderColor: 'blue.300',
                              }}
                              transition="all 0.2s"
                            >
                              {(productERP.value_plan ?? productERP.value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </Box>
                          </Tooltip>
                        </Box>

                        {/* Usuários Ativos */}
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          borderRight="1px solid"
                          borderColor="gray.200"
                          py={2}
                          width="120px"
                          minW="120px"
                          maxW="120px"
                        >
                          <Tooltip label="Total de usuários ativos atualmente neste plano ERP" placement="top">
                            <Badge colorScheme="green" fontSize="xs" variant="solid" textAlign="center">
                              {(productERP.count_clients_active ?? productERP.count_clients ?? 0).toLocaleString('pt-BR')}
                            </Badge>
                          </Tooltip>
                        </Box>

                        {/* Colunas dos Produtos */}
                        {sortedProducts.map((product) => {
                          return (
                            <Box
                              key={product.name}
                              textAlign="center"
                              py={2}
                              borderRight="1px solid"
                              borderColor="gray.200"
                              width="150px"
                              minW="150px"
                              maxW="150px"
                            >
                              <HStack spacing={1} flexWrap="wrap" w="100%" alignItems="center" justifyContent="center">
                        {product.plans
                                  .sort((a, b) => a.level.rank - b.level.rank)
                                  .map((planFound) => {
                                    const badge = badges.find((badge: any) => badge.level === planFound.level.rank);

                            const isCurrentPlan = productERP.products?.some(
                              (p: any) => p.product === product.id && p.plan === planFound.id,
                            );

                                    if (badge) {
                                      return (
                                        <Tooltip
                                          key={planFound.id}

                                          placement="top"
                                        >
                                          <Button
                                            variant="ghost"
                                            size="xs"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                            }}
                                            position="relative"
                                            p={1}
                                            minW="auto"
                                            _hover={{
                                              transform: 'scale(1.05)',
                                              bg: isCurrentPlan ? 'green.100' : 'blue.50',
                                            }}
                                            transition="all 0.2s"
                                            opacity={1}
                                          >
                                            <VStack spacing={0} position="relative">
                                              <Box position="relative">
                                                <Image
                                                  src={`/${badge.image}`}
                                                  alt={badge.name}
                                                  style={{ height: '20px', width: 'auto' }}
                                                  opacity={isCurrentPlan ? 1 : 0.3}
                                                  filter={isCurrentPlan ? 'none' : 'grayscale(50%)'}
                                                />

                                              </Box>
                                            </VStack>
                                          </Button>
                                        </Tooltip>
                                      );
                                    }
                                    return null;
                                  })}
                              </HStack>
                              {product.plans.length === 0 && (() => {
                                const currentProductFound = productERP.products.find((p: any) => p.product === product.id);
                                const checked = !!currentProductFound;
                                return (
                                  <Checkbox
                                    size="lg"
                                    isChecked={checked}
                                    readOnly
                                  />
                                );
                              })()}
                            </Box>
                          );
                        })}
                      </Box>
                    </AccordionButton>

                    <AccordionPanel p={0} bg="gray.50">
                      <Box p={4}>
                        <Text fontSize="sm" color="gray.600">
                          Revise os produtos vinculados a este plano na tabela acima. O simulador de medalhas está disponível no próximo passo.
                        </Text>
                      </Box>
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
            ))}
          </Accordion>
          </Box>
        </TableContainer>
      </Card>
    </VStack>
  );
};
